import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { T } from '@tolgee/react';
import { getBlogPosts, getStrapiMediaUrl } from '../../../lib/strapi';
import type { StrapiBlogPost } from '../../../lib/strapi/services';

// Componente principal de la página de blog
const BlogPage = () => {
  const [posts, setPosts] = useState<StrapiBlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Obtener posts desde Strapi
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const strapiPosts = await getBlogPosts(20); // Obtenemos hasta 20 posts
        setPosts(strapiPosts);
        setError(null);
      } catch (err) {
        console.error("Error fetching blog posts:", err);
        setError("blog_error_loading");
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Formatear fecha
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  // Extraer categorías únicas de los posts
  const categories = Array.from(
    new Set(posts.map(post => post.category?.name))
  ).filter(Boolean) as string[];

  // Filtrar posts por categoría
  const filteredPosts = selectedCategory
    ? posts.filter(post => post.category?.name === selectedCategory)
    : posts;

  // Post destacado (el primero de la lista)
  const featuredPost = posts.length > 0 ? posts[0] : null;

  // Resto de posts
  const regularPosts = selectedCategory ? filteredPosts : (posts.length > 1 ? filteredPosts.slice(1) : []);

  return (
    <>
      <Helmet>
        <title><T keyName="blog.page_title">Blog | Jarad - Nutrición Premium para Mascotas</T></title>
        <meta name="description" content="Consejos de expertos para el bienestar, alimentación y cuidado de tus mascotas. Descubre artículos sobre nutrición animal y cuidados especializados." />
      </Helmet>

      <div style={{ backgroundColor: '#f2ede4' }} className="min-h-screen">
        {/* Hero Section */}
        <div className="relative h-[70vh] overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10"></div>
            <img
              src="/blog/hero-bg.webp"
              alt="Mascotas felices"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-20 h-full flex flex-col justify-center items-center text-white px-4 pt-16">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-extrabold text-center mb-4 tracking-tight"
            >
              <T keyName="blog.hero_title">Blog Jarad</T>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl text-center max-w-3xl mb-8 font-light"
            >
              <T keyName="blog.hero_subtitle">Consejos de expertos para el bienestar, alimentación y cuidado de tus mascotas</T>
            </motion.p>
          </div>
        </div>

        <div className="py-16">
          <div className="content-container">
            {/* Mensaje de error */}
            {error && (
              <div className="text-center py-12 text-red-600">
                <p>{error}</p>
              </div>
            )}

            {/* Filtro de categorías */}
            {categories.length > 0 && (
              <motion.div
                className="flex flex-wrap justify-center mb-16"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`mx-3 py-3 px-6 text-sm font-medium rounded-full transition-all duration-300 ${selectedCategory === null
                    ? 'text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  style={{
                    backgroundColor: selectedCategory === null ? '#f6cf02' : undefined,
                    color: selectedCategory === null ? '#0d0d0d' : undefined
                  }}
                >
                  <T keyName="blog.filter_all">TODOS</T>
                </button>
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`mx-3 py-3 px-6 text-sm font-medium rounded-full transition-all duration-300 ${selectedCategory === category
                      ? 'text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    style={{
                      backgroundColor: selectedCategory === category ? '#f6cf02' : undefined,
                      color: selectedCategory === category ? '#0d0d0d' : undefined
                    }}
                  >
                    {category.toUpperCase()}
                  </button>
                ))}
              </motion.div>
            )}

            {/* Estado de carga */}
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2" style={{ borderColor: '#f6cf02' }}></div>
              </div>
            ) : (
              <>
                {/* Post destacado solo si no hay filtro */}
                {!selectedCategory && featuredPost && (
                  <motion.div
                    className="mb-20"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                  >
                    <Link to={`/blog/${featuredPost.slug}`} className="block group">
                      <div className="bg-white rounded-2xl shadow-md overflow-hidden hoverable-card">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                          <div className="aspect-[4/3] overflow-hidden">
                            <img
                              src={getStrapiMediaUrl(featuredPost.image)}
                              alt={featuredPost.title || ''}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="p-8">
                            <span className="text-xs tracking-widest uppercase text-gray-500 mb-3 inline-block">
                              {featuredPost.category?.name || 'Blog'}
                            </span>
                            <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#0d0d0d' }}>
                              {featuredPost.title || featuredPost.Title || ''}
                            </h2>
                            <p className="text-gray-600 mb-4 leading-relaxed">
                              {featuredPost.excerpt || ''}
                            </p>
                            <div className="flex items-center text-sm text-gray-500">
                              <span>{featuredPost.publishedAt ? formatDate(featuredPost.publishedAt) : ''}</span>
                              <span className="mx-2">•</span>
                              <span><T keyName="blog.read_time">5 min de lectura</T></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                )}

                {/* Lista de posts en grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {regularPosts.map((post, index) => (
                    <motion.article
                      key={post.id}
                      className="group flex flex-col h-full"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                    >
                      <Link to={`/blog/${post.slug}`} className="block flex-1">
                        <div className="bg-white rounded-2xl shadow-md overflow-hidden hoverable-card h-full flex flex-col">
                          <div className="aspect-[4/3] overflow-hidden">
                            <img
                              src={getStrapiMediaUrl(post.image)}
                              alt={post.title || ''}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                          <div className="p-6 flex-1 flex flex-col">
                            <span className="text-xs tracking-widest uppercase text-gray-500 mb-2 inline-block">
                              {post.category?.name || 'Blog'}
                            </span>
                            <h3 className="text-lg font-bold mb-3 group-hover:text-gray-700 transition-colors flex-1" style={{ color: '#0d0d0d' }}>
                              {post.title || post.Title || ''}
                            </h3>
                            <p className="text-gray-600 mb-4 line-clamp-2 leading-relaxed text-sm">
                              {post.excerpt || ''}
                            </p>
                            <div className="flex items-center text-sm text-gray-500 mt-auto">
                              <span>{post.publishedAt ? formatDate(post.publishedAt) : ''}</span>
                              <span className="mx-2">•</span>
                              <span><T keyName="blog.read_time">5 min de lectura</T></span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  ))}
                </div>

                {/* Mensaje si no hay posts */}
                {filteredPosts.length === 0 && !loading && (
                  <div className="text-center py-20">
                    <p className="text-gray-600 text-lg"><T keyName="blog.no_posts_category">No hay artículos disponibles en esta categoría.</T></p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Newsletter sección */}
        <div className="py-16 bg-white">
          <div className="content-container">
            <motion.div
              className="max-w-2xl mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#0d0d0d' }}>
                <T keyName="blog.newsletter_title">Suscríbete a nuestro newsletter</T>
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                <T keyName="blog.newsletter_description">Recibe nuestras últimas publicaciones, tendencias y ofertas exclusivas directamente en tu correo.</T>
              </p>
              <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="blog.newsletter_placeholder"
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-400 transition-colors"
                  style={{ color: '#0d0d0d' }}
                />
                <button
                  type="submit"
                  className="px-8 py-3 rounded-full font-medium transition-all border-2 shadow-md hover:shadow-lg"
                  style={{
                    backgroundColor: '#f6cf02',
                    color: '#0d0d0d',
                    borderColor: '#0d0d0d'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0d0d0d';
                    e.currentTarget.style.color = '#f6cf02';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f6cf02';
                    e.currentTarget.style.color = '#0d0d0d';
                  }}
                >
                  <T keyName="blog.newsletter_subscribe">Suscribirse</T>
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPage; 